import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { FiFilter, FiX, FiGrid } from 'react-icons/fi';
import { optimizedAPI } from '../lib/optimizedApi';

export default function CategoryPage() {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    price_min: '',
    price_max: '',
    search: '',
    sort: 'date',
  });
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  const perPage = 8;

  useEffect(() => {
    const search = searchParams.get('search') || '';
    setFilters(prev => ({
      ...prev,
      search,
    }));
    setCurrentPage(1);
    setProducts([]);
    setHasMore(true);
  }, [searchParams]);


  useEffect(() => {
    fetchProducts(true);
  }, [category, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProducts = async (reset = false) => {
    if (reset) {
      setLoading(true);
      setCurrentPage(1);
      setProducts([]);
      setHasMore(true);
    } else {
      setLoadingMore(true);
      setCurrentPage(prev => prev + 1);
    }

    try {
      // Use optimized API for category-specific collections
      const apiParams = {
        per_page: perPage,
        page: reset ? 1 : currentPage + 1,
        search: filters.search || undefined,
        min_price: filters.price_min || undefined,
        max_price: filters.price_max || undefined,
        sort: filters.sort
      };

      // Use optimized API for category collection
      const response = await optimizedAPI.getCategoryCollection(category, apiParams);
      const fetchedProducts = response.products || [];
      
      if (reset) {
        setProducts(fetchedProducts);
      } else {
        // Prevent duplicates by filtering out products that already exist
        setProducts(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const newProducts = fetchedProducts.filter(p => !existingIds.has(p.id));
          return [...prev, ...newProducts];
        });
      }
      
      // Check if there are more products
      setHasMore(response.has_more || false);
    } catch (error) {
      console.error('Error fetching products:', error);
      if (reset) {
        setProducts([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    // Clear existing search timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // If search is being changed, debounce it
    if (newFilters.hasOwnProperty('search')) {
      const timeout = setTimeout(() => {
        setFilters(prev => ({ ...prev, ...newFilters }));
      }, 500); // 500ms debounce
      setSearchTimeout(timeout);
    } else {
      // For other filters, update immediately
      setFilters(prev => ({ ...prev, ...newFilters }));
    }
  };

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      fetchProducts(false);
    }
  };

  // Infinite scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1000) {
        loadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMore, currentPage, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const getCategoryTitle = () => {
    if (!category) return 'All Products';
    return category.charAt(0).toUpperCase() + category.slice(1).replace('-', ' ');
  };

  return (
    <div className="min-h-screen bg-black">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-xl md:text-2xl font-bold text-white">
              {getCategoryTitle()} Collection
            </h1>
            
            {/* Back to Collections Button */}
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center px-3 py-1.5 text-white font-semibold rounded-lg transition-colors border border-gray-600 hover:border-pink-500 text-sm"
            >
              <FiGrid className="mr-1.5" size={14} />
              Back to Collections
            </button>
          </div>
          <p className="text-gray-400 text-sm">
            Discover our {getCategoryTitle().toLowerCase()} collection
          </p>
        </div>

        {/* Top Filter Bar */}
        <div className="mb-6">
          <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              {/* Filter Toggle Button */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 text-white font-semibold rounded-lg transition-colors bg-gray-800 hover:bg-gray-700 border border-gray-600 text-sm"
              >
                {showFilters ? <FiX className="mr-1.5" size={16} /> : <FiFilter className="mr-1.5" size={16} />}
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>

              {/* Quick Filter Options */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleFilterChange({ sort: 'date' })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    filters.sort === 'date' 
                      ? 'bg-pink-600 text-white' 
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  Newest
                </button>
                <button
                  onClick={() => handleFilterChange({ sort: 'price' })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    filters.sort === 'price' 
                      ? 'bg-pink-600 text-white' 
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  Price
                </button>
                <button
                  onClick={() => handleFilterChange({ sort: 'popularity' })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    filters.sort === 'popularity' 
                      ? 'bg-pink-600 text-white' 
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  Popular
                </button>
              </div>
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-white font-semibold mb-1.5 text-sm">Price Range</label>
                    <div className="flex space-x-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={filters.price_min}
                        onChange={(e) => handleFilterChange({ price_min: e.target.value })}
                        className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm"
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        value={filters.price_max}
                        onChange={(e) => handleFilterChange({ price_max: e.target.value })}
                        className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-white font-semibold mb-1.5 text-sm">Search</label>
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange({ search: e.target.value })}
                      className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={() => setFilters({
                        price_min: '',
                        price_max: '',
                        search: '',
                        sort: 'date',
                      })}
                      className="w-full px-3 py-1.5 text-white font-semibold rounded-lg transition-colors border border-gray-600 hover:border-pink-500 text-sm"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Products Grid */}
        <div className="w-full">
          {loading ? (
            <div className="w-full">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {[...Array(10)].map((_, index) => (
                  <div key={index} className="bg-gray-900 rounded-xl overflow-hidden animate-pulse flex flex-col">
                    <div className="bg-gray-800 flex-1" style={{ height: '250px' }}></div>
                    <div className="p-3 bg-gray-900 flex-shrink-0" style={{ minHeight: '80px' }}>
                      <div className="space-y-2">
                        <div className="bg-gray-800 h-4 rounded w-3/4"></div>
                        <div className="bg-gray-800 h-5 rounded w-1/2"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 text-sm mb-4">
                No products found in this category.
              </div>
              <button
                onClick={() => setFilters({
                  price_min: '',
                  price_max: '',
                  search: '',
                  sort: 'date',
                })}
                className="px-4 py-2 text-white font-semibold rounded-lg transition-colors text-sm"
                style={{ backgroundColor: '#CF0F63' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#B00D56'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#CF0F63'}
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div className="w-full">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              </div>
              
              {/* Load More Button */}
              {hasMore && !loadingMore && (
                <div className="text-center mt-8">
                  <button
                    onClick={loadMore}
                    className="px-6 py-2.5 text-white font-semibold rounded-lg transition-colors text-sm"
                    style={{ backgroundColor: '#CF0F63' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#B00D56'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#CF0F63'}
                  >
                    Load More Products
                  </button>
                </div>
              )}

              {/* Loading More Indicator */}
              {loadingMore && (
                <div className="text-center mt-6">
                  <div className="inline-flex items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pink-500 mr-2"></div>
                    <span className="text-gray-400 text-sm">Loading more products...</span>
                  </div>
                </div>
              )}

              {/* End of Results */}
              {!hasMore && products.length > 0 && (
                <div className="text-center mt-8">
                  <p className="text-gray-400 text-sm">You've reached the end of this collection!</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}